import React from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {Alert} from '@mui/material';
import {
  Body,
  Button,
  Headline,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Modal from '@/components/modal';
import useCopyToClipboardWithFeedback from '@/hooks/useCopyToClipboardWithFeedback';
import styles from './InviteLinkModal.module.scss';

const COPY_FEEDBACK_RESET_MS = 2000;

interface InviteLinkModalProps {
  open: boolean;
  onClose: () => void;
  campaignName: string;
  inviteLink: string;
}

function InviteLinkModal({
  open,
  onClose,
  campaignName,
  inviteLink,
}: InviteLinkModalProps) {
  const {copied, copyToClipboard} = useCopyToClipboardWithFeedback(
    COPY_FEEDBACK_RESET_MS
  );

  const handleCopyLink = () => {
    copyToClipboard(inviteLink);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      contentClassName={styles.modal}
    >
      <div className={styles.content}>
        <Headline size="md" sx={{width: '100%'}}>
          {translate('Invite to {campaignName}', {campaignName})}
        </Headline>
        <Body
          size="md"
          sx={{
            width: '100%',
            color: designSystemToken('semantic.fg.secondary'),
          }}
        >
          {translate(
            'Share this link to allow anyone to apply for this campaign'
          )}
        </Body>
        <Alert
          severity="warning"
          variant="standard"
          icon={<WarningAmberOutlinedIcon />}
          className={styles.warningAlert}
        >
          {translate(
            'Anyone with this link will be able to apply for the campaign regardless of brief requirements'
          )}
        </Alert>
        <div className={styles.linkDisplay}>
          <Body size="lg">{inviteLink}</Body>
        </div>
        <div className={styles.footerActions}>
          <Button
            size="large"
            appearance="neutral"
            mode="tinted"
            onClick={handleCopyLink}
            startIcon={
              !copied ? (
                <Icon appearance="neutral" name="Navigation-Copy" />
              ) : null
            }
          >
            {copied ? translate('Copied!') : translate('Copy Link')}
          </Button>
          <Button
            size="large"
            appearance="neutral"
            mode="filled"
            onClick={onClose}
          >
            {translate('Done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default InviteLinkModal;
